import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Button, TextField, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Tooltip, Chip, Collapse, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const GROUP_ORDER = ['Adult', 'Junior', 'Camps & Clinics'];
const GROUP_COLORS = { Adult: 'primary', Junior: 'success', 'Camps & Clinics': 'info' };

function InlineEdit({ value, prefix, placeholder, type, onSave }) {
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);
  useEffect(() => setVal(value), [value]);
  async function save() { setSaving(true); await onSave(val); setSaving(false); }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField size="small" type={type || 'text'} value={val} placeholder={placeholder}
                 onChange={e => setVal(e.target.value)}
                 InputProps={{ startAdornment: prefix ? <Typography variant="body2" sx={{ mr: 0.5 }}>{prefix}</Typography> : null }}
                 sx={{ width: 110 }} />
      <IconButton size="small" onClick={save} disabled={saving} color="primary">
        {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}

export default function TournamentDivisionManagement() {
  const navigate = useNavigate();
  const [divisions, setDivisions] = useState([]);
  // groupDefaults: { adult: { late_fee: 20, entry_fee: 80, cap: 8 }, junior: {...}, ... }
  const [groupDefaults, setGroupDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('Adult');
  const [newPrice, setNewPrice] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadDivisions(); }, []);

  async function loadDivisions() {
    const snap = await getDocs(query(collection(db, 'divisions'), orderBy('sort_order')));
    setDivisions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    // Group-level defaults (settings/division_groups). Schema:
    //   { late_fees: { Adult: 20 }, entry_fees: { Adult: 80 }, caps: { Adult: 8 } }
    try {
      const groupsSnap = await getDoc(doc(db, 'settings', 'division_groups'));
      const data = groupsSnap.exists() ? groupsSnap.data() : {};
      // Re-shape to nested per-group: { Adult: { late_fee, entry_fee, cap }, ... }
      const merged = {};
      const lateFees  = data.late_fees  || {};
      const entryFees = data.entry_fees || {};
      const caps      = data.caps       || {};
      for (const g of new Set([...Object.keys(lateFees), ...Object.keys(entryFees), ...Object.keys(caps)])) {
        merged[g] = {
          late_fee:  lateFees[g]  != null ? Number(lateFees[g])  : null,
          entry_fee: entryFees[g] != null ? Number(entryFees[g]) : null,
          cap:       caps[g]      != null ? Number(caps[g])      : null,
        };
      }
      setGroupDefaults(merged);
    } catch (e) {
      console.error('Failed to load group defaults:', e);
    }
    setLoading(false);
  }

  // Save one field on a group default and persist to settings/division_groups
  async function saveGroupDefault(groupName, field, value) {
    // field: 'late_fee' | 'entry_fee' | 'cap'
    const numValue = value === '' ? null : Number(value);
    const next = { ...groupDefaults, [groupName]: { ...(groupDefaults[groupName] || {}), [field]: numValue } };
    setGroupDefaults(next);
    // Re-shape into the flat-per-field maps for storage
    const fieldKey = field === 'late_fee' ? 'late_fees' : field === 'entry_fee' ? 'entry_fees' : 'caps';
    const flatMap = {};
    for (const [g, defs] of Object.entries(next)) {
      if (defs[field] != null) flatMap[g] = defs[field];
    }
    await setDoc(doc(db, 'settings', 'division_groups'), { [fieldKey]: flatMap }, { merge: true });
  }

  async function saveDivLateFee(div, v) {
    const lateFee = v === '' ? null : Number(v);
    await updateDoc(doc(db, 'divisions', div.id), { default_late_fee: lateFee });
    setDivisions(ds => ds.map(d => d.id === div.id ? { ...d, default_late_fee: lateFee } : d));
  }

  async function addDivision(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const ref = await addDoc(collection(db, 'divisions'), {
        name: newName.trim(), group_name: newGroup,
        price: Number(newPrice || 0), cap: null, sort_order: divisions.length,
      });
      setDivisions(d => [...d, { id: ref.id, name: newName.trim(), group_name: newGroup, price: Number(newPrice||0), cap: null, sort_order: divisions.length }]);
      setNewName(''); setNewPrice(''); setAddOpen(false);
    } catch(e) { alert('Add failed: ' + e.message); }
    finally { setAdding(false); }
  }

  async function savePrice(div, v) {
    const price = Number(v);
    await updateDoc(doc(db, 'divisions', div.id), { price });
    setDivisions(ds => ds.map(d => d.id === div.id ? { ...d, price } : d));
  }

  async function saveCap(div, v) {
    const cap = v ? Number(v) : null;
    await updateDoc(doc(db, 'divisions', div.id), { cap });
    setDivisions(ds => ds.map(d => d.id === div.id ? { ...d, cap } : d));
  }

  async function deleteDivision(div) {
    if (!window.confirm(`Delete "${div.name}"?`)) return;
    await deleteDoc(doc(db, 'divisions', div.id));
    setDivisions(ds => ds.filter(d => d.id !== div.id));
  }

  const grouped = {};
  divisions.forEach(d => {
    const g = d.group_name || 'Adult';
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(d);
  });
  const groupedEntries = [
    ...GROUP_ORDER.filter(g => grouped[g]).map(g => [g, grouped[g]]),
    ...Object.entries(grouped).filter(([g]) => !GROUP_ORDER.includes(g)),
  ];

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">🗂 Division Management</Typography>
        </Container>
      </Box>
      <Container sx={{ pb: 6 }}>

        {/* Add Division */}
        <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: addOpen ? '1px solid' : 'none', borderColor: 'divider' }}>
            <Typography fontWeight="bold">➕ Add Division</Typography>
            <Button size="small" variant="outlined" endIcon={addOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setAddOpen(o => !o)}>
              {addOpen ? 'Hide' : 'Add'}
            </Button>
          </Box>
          <Collapse in={addOpen}>
            <Box sx={{ p: 3 }}>
              <form onSubmit={addDivision}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth size="small" label="Division Name" placeholder="e.g. Men's Open"
                               value={newName} onChange={e => setNewName(e.target.value)} required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Group</InputLabel>
                      <Select value={newGroup} label="Group" onChange={e => setNewGroup(e.target.value)}>
                        <MenuItem value="Adult">Adult</MenuItem>
                        <MenuItem value="Junior">Junior</MenuItem>
                        <MenuItem value="Camps & Clinics">Camps & Clinics</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField fullWidth size="small" label="Entry Fee ($)" type="number"
                               inputProps={{ step:'0.01', min:0 }} placeholder="0.00"
                               value={newPrice} onChange={e => setNewPrice(e.target.value)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Button type="submit" variant="contained" color="success" fullWidth
                            startIcon={adding ? null : <AddIcon />} disabled={adding}>
                      {adding ? <CircularProgress size={20} color="inherit" /> : 'Add'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Collapse>
        </Card>

        {/* Division groups */}
        {groupedEntries.map(([groupName, divs]) => {
          const gDefaults = groupDefaults[groupName] || {};
          return (
          <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }} key={groupName}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Chip label={groupName} color={GROUP_COLORS[groupName] || 'default'} size="small" />
                <Typography fontWeight="bold">{groupName} Divisions</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', pl: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Group Defaults:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Entry Fee</Typography>
                  <InlineEdit value={gDefaults.entry_fee != null ? Number(gDefaults.entry_fee).toFixed(2) : ''}
                              prefix="$" placeholder="0.00" type="number"
                              onSave={v => saveGroupDefault(groupName, 'entry_fee', v)} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Late Fee</Typography>
                  <InlineEdit value={gDefaults.late_fee != null ? Number(gDefaults.late_fee).toFixed(2) : ''}
                              prefix="$" placeholder="0.00" type="number"
                              onSave={v => saveGroupDefault(groupName, 'late_fee', v)} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cap</Typography>
                  <InlineEdit value={gDefaults.cap != null ? String(gDefaults.cap) : ''}
                              placeholder="∞" type="number"
                              onSave={v => saveGroupDefault(groupName, 'cap', v)} />
                </Box>
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Division</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Entry Fee Override</TableCell>
                  <TableCell>Late Fee Override</TableCell>
                  <TableCell>Cap Override</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {divs.map(div => (
                  <TableRow key={div.id}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{div.name}</TableCell>
                    <TableCell><Chip label={div.group_name} color={GROUP_COLORS[div.group_name] || 'default'} size="small" /></TableCell>
                    <TableCell>
                      <InlineEdit
                        value={div.price != null && div.price !== 0 ? Number(div.price).toFixed(2) : ''}
                        prefix="$"
                        placeholder={gDefaults.entry_fee != null ? `${Number(gDefaults.entry_fee).toFixed(2)} (group)` : '0.00'}
                        type="number"
                        onSave={v => savePrice(div, v)}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineEdit
                        value={div.default_late_fee != null ? Number(div.default_late_fee).toFixed(2) : ''}
                        prefix="$"
                        placeholder={gDefaults.late_fee != null ? `${Number(gDefaults.late_fee).toFixed(2)} (group)` : 'inherit'}
                        type="number"
                        onSave={v => saveDivLateFee(div, v)}
                      />
                    </TableCell>
                    <TableCell>
                      <InlineEdit
                        value={div.cap || ''}
                        placeholder={gDefaults.cap != null ? `${gDefaults.cap} (group)` : '∞'}
                        type="number"
                        onSave={v => saveCap(div, v)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => deleteDivision(div)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          );
        })}

        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>
          Back to Dashboard
        </Button>
      </Container>
    </Box>
  );
}
